/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, SortDirection, Sorting, StandardLoadOptions, State } from 'app/shared/lib/essette-site';
import { ICorrespondenceConfig } from 'app/core/models/essette-interfaces';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { CorrespondenceService } from 'app/core/services/correspondence-service';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { Toaster } from 'app/shared/lib/toaster';
import { I18N } from 'aurelia-i18n';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class CorrespondenceHistoryGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected householdCaseId!: string;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected allowRetry: boolean = true;

    @bindable.object({ defaultBindingMode: bindingMode.toView })
    protected config!: ICorrespondenceConfig;

    protected canPreviewDownloads: boolean = false;
    protected showMemberSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;
    protected showCaseSummaryLink: boolean = false;
    protected hasCaseRoute = false;
    protected openCaseSummary = openCaseSummary;


    protected letters: Core.CorrespondenceLetter[] = [];
    protected stateRetry = new State();
    protected sorting = new Sorting();
    protected resultCount = 0;
    protected pageSize = 25;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: CorrespondenceService,
        private readonly transitional: TransitionalManager,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        if(!this.householdCaseId) { throw new Error('Bindable property [household-case-id] was not set'); }
        this.service.configure(this.config);
        this.checkPermissions();
        this.reload();
    }


    ////****************** Events ***************************************************////
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }


    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadHistoryLetters();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
        this.canPreviewDownloads = this.accountContext.hasPermission('CM.CaseCorrespondenceLetter.Download.Preview');
    }

    protected openLetterCopyPreview(copy: Core.CorrespondenceLetterCopy) {
        if(!copy || !copy.letter || !this.config.previewTransitionalPageId) { return; }
        const w: Window | null = open('', 'Popup_PreviewLetter', `top=25,left=${(screen.width / 4)},width=1000,height=${(screen.height - 150)}`);
        const transitionalIds = [copy.letter.id!, copy.id!];
        this.transitional.openTransitional(w, this.config.previewTransitionalPageId, transitionalIds);
    }

    protected openLetterPreview(letter: Core.CorrespondenceLetter) {
        if(!letter || !this.config.previewTransitionalPageId) { return; }
        const w: Window | null = open('', 'Popup_PreviewLetter', `top=25,left=${(screen.width / 4)},width=1000,height=${(screen.height - 150)}`);
        const transitionalIds: string[] = [letter.id!, ''];
        this.transitional.openTransitional(w, this.config.previewTransitionalPageId, transitionalIds);
    }

    protected applyTableDisplay(letters: Core.CorrespondenceLetter[]): Core.CorrespondenceLetter[] {
        return letters.map((letter) => {
            return {
                ...letter,
                $essTableDisplay: this.i18n.tr('ui:labels.for', { what: letter.template?.description, item: letter.description })
            } as Core.CorrespondenceLetter;
        });
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async retryLetterCopy(copy: Core.CorrespondenceLetterCopy) {
        let isSuccessful = false;
        try {
            this.state.beginSave();

            const response = await this.service.resendLetterCopy(copy);
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            throw error;
        }
        finally {
            if(isSuccessful) {
                this.loadHistoryLetters();
                this.toaster.info().message(this.i18n.tr('core:correspondenceLetter.notifications.queuedPrinting')).show();
            } else {
                this.toaster.error().message(this.i18n.tr('core:correspondenceLetter.notifications.failedRetry')).show();
            }
            this.state.endSave();
        }
    }

    @apiCall
    private async loadHistoryLetters(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0
        };

        const includes: string[] = [
            'template',
            'template.version',
            'template.version.language',
            'status',
            'copies',
            'copies.deliveryMethod',
            'copies.delivered.recipient',
            'copies.status',
            'copies.coverSheet.templateVersion.template',
            'metadata.modified.byUser',
            'case',
            'case.member'
        ];

        try {
            this.state.beginLoad();

            const response = await this.service.loadLettersByTarget(this.householdCaseId, true, options, includes);

            this.letters = this.applyTableDisplay(response.results);
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
